/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal } from 'antd';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { Fragment } from 'react';

type HelpModalProps = {
  visible: boolean;
  onClose: () => void;
  headerId: string;
};

const HelpEN = () => (
  <Fragment>
    <div>
      <span className="help-head">Project Builder</span>
      <span className="help-text">
        Project Builder allows project administrators to manage the folder
        structure for the current project's archive before saving the changes to
        the server.
      </span>
      <span className="help-text">
        Unlike the archive itself, Project Builder displays the complete folder
        hierarchy in a single view, allowing you to review and edit the entire
        project structure without opening individual folders.
      </span>
      <span className="help-text">
        All changes remain local until <strong>Save All</strong> is selected.
      </span>
      <br />

      <span className="help-sub-head">Before You Begin</span>
      <span className="help-text">
        Project Builder is only available to project administrators.
      </span>
      <span className="help-text">
        Changes made in Project Builder are not applied immediately. You can
        make multiple changes and review the complete folder structure before
        saving.
      </span>
      <span className="help-text">
        When you're satisfied with the result, select <strong>Save All</strong>{' '}
        to apply all pending changes.
      </span>
      <br />

      <span className="help-sub-head">Working with Existing Folders</span>
      <span className="help-text">
        Existing folders already stored on the server have some limitations.
      </span>
      <span className="help-text">
        <strong>You can:</strong>
      </span>
      <span className="help-text">• Rename a folder.</span>
      <span className="help-text">• Edit its description.</span>
      <span className="help-text">• Add new subfolders.</span>
      <span className="help-text">
        • Import additional folder structures beneath it.
      </span>

      <span className="help-text">
        <strong>You cannot:</strong>
      </span>
      <span className="help-text">
        • Move existing folders to another location.
      </span>
      <span className="help-text">
        • Remove existing folders from Project Builder.
      </span>
      <span className="help-text">
        Any edits remain pending until <strong>Save All</strong> is selected.
      </span>
      <br />

      <span className="help-sub-head">Creating New Folders</span>
      <span className="help-text">
        Select <strong>+ New Folder</strong> in the upper-right corner to create
        a new folder at the root of the current project.
      </span>
      <span className="help-text">You'll be prompted to enter:</span>
      <span className="help-text">• Name (required)</span>
      <span className="help-text">• Description (optional)</span>
      <span className="help-text">
        Select <strong>Save</strong> in the dialog to add the folder to Project
        Builder.
      </span>
      <span className="help-text">
        New folders are not created on the server until{' '}
        <strong>Save All</strong> is selected.
      </span>
      <br />

      <span className="help-sub-head">Importing Folder Structures</span>
      <span className="help-text">
        The Import feature allows you to copy an existing folder structure from
        another project or a template.
      </span>

      <span className="help-sub-head">Step 1 – Select a Source</span>
      <span className="help-text">
        Choose the project or template you want to import from.
      </span>

      <span className="help-sub-head">Step 2 – Select Folders</span>
      <span className="help-text">
        Browse the available folder structure and select the folders you want to
        import. Selecting a folder automatically includes all of its subfolders.
      </span>
      <span className="help-text">
        <strong>Use:</strong>
      </span>
      <span className="help-text">
        • <strong>Select All</strong> to include the entire structure.
      </span>
      <span className="help-text">
        • <strong>Deselect All</strong> to clear your selection.
      </span>
      <span className="help-text">
        When finished, select <strong>Import to Project Builder</strong>.
      </span>
      <span className="help-text">
        Imported folders are added as new folders and can be edited or moved
        until they are saved.
      </span>
      <br />

      <span className="help-sub-head">
        Adding Folders Inside Another Folder
      </span>
      <span className="help-text">
        Instead of adding folders to the project root, you can create or import
        folders directly beneath an existing folder.
      </span>
      <span className="help-text">Open the folder's context menu by:</span>
      <span className="help-text">• Right-clicking the folder, or</span>
      <span className="help-text">
        • Selecting the <strong>⋮</strong> menu on the right side of the row.
      </span>
      <span className="help-text">Choose either:</span>
      <span className="help-text">
        • <strong>New Folder</strong>
      </span>
      <span className="help-text">
        • <strong>Import</strong>
      </span>
      <span className="help-text">
        The selected folder becomes the destination, and the dialog displays the
        target location before the folder is created or imported.
      </span>
      <br />

      <span className="help-sub-head">Editing Folders</span>
      <span className="help-text">
        All folders can be edited before saving.
      </span>
      <span className="help-text">
        Choose <strong>Edit</strong> from the folder's context menu to change:
      </span>
      <span className="help-text">• Name</span>
      <span className="help-text">• Description</span>
      <span className="help-text">
        Changes remain pending until <strong>Save All</strong> is selected.
      </span>
      <br />

      <span className="help-sub-head">Moving New Folders</span>
      <span className="help-text">
        Only newly created or imported folders can be moved. You can move them
        by:
      </span>
      <span className="help-text">
        • Dragging and dropping the folder to a new parent, or
      </span>
      <span className="help-text">
        • Selecting <strong>Move To</strong> from the folder's context menu.
      </span>
      <span className="help-text">
        Moving a folder also moves all of its subfolders. Existing folders
        cannot be moved.
      </span>
      <br />

      <span className="help-sub-head">Removing New Folders</span>
      <span className="help-text">
        Only folders that have not yet been saved can be removed.
      </span>
      <span className="help-text">
        Select <strong>Remove</strong> from the folder's context menu to delete
        the selected folder together with all of its subfolders.
      </span>
      <span className="help-text">
        Existing folders cannot be removed from Project Builder.
      </span>
      <br />

      <span className="help-sub-head">Saving Your Changes</span>
      <span className="help-text">
        Project Builder keeps all modifications as pending changes while you
        work. This includes:
      </span>
      <span className="help-text">• New folders</span>
      <span className="help-text">• Imported folders</span>
      <span className="help-text">• Renamed folders</span>
      <span className="help-text">• Updated descriptions</span>
      <span className="help-text">• Folder moves</span>
      <span className="help-text">
        No changes are applied to the project until you select{' '}
        <strong>Save All</strong>.
      </span>
      <span className="help-text">
        If you leave Project Builder before saving, any unsaved changes will be
        discarded.
      </span>
      <br />

      <span className="help-sub-head">Tips</span>
      <span className="help-text">
        • Review the complete folder structure before saving.
      </span>
      <span className="help-text">
        • Existing folders remain fixed in their original location.
      </span>
      <span className="help-text">
        • New folders can be reorganized freely until saved.
      </span>
      <span className="help-text">
        • Imported folders behave like newly created folders until they are
        saved.
      </span>
      <span className="help-text">
        • Use the context menu to quickly add, edit, move, or remove folders
        without leaving the current view.
      </span>
    </div>
  </Fragment>
);

const HelpDA = () => (
  <Fragment>
    <div className="helpContent">
      <span className="help-head">Projekt Builder</span>
      <span className="help-text">
        Projekt Builder giver projektets administrative brugere mulighed for at
        administrere mappestrukturen for arkivet på det aktuelle projekts, danne
        et overblik og gennemgå ændringerne fær de til sidst gemmes på serveren.
      </span>
      <span className="help-text">
        I Projekt Builder er der et overblik over det komplette mappehierarki
        for hele projektet i en enkelt visning, så hele projektstrukturen kan
        gennemgås og redigeres uden at de enkelte mapper skal åbnes i arkivet.
      </span>
      <span className="help-text">
        Alle ændringer der bliver foretaget i Projekt Builder vil træde i kraft
        før strukturen som helhed gemmes, derfor er der ikke unødvendige ubrugte
        slettede mapper og log beskeder ved eventuelle fejloprettelser eller
        ændringer.
      </span>

      <span className="help-sub-head">Før du begynder</span>
      <span className="help-text">
        Projekt Builder funktionerne er kun tilgængelig for administrative
        brugere på projektet
      </span>
      <span className="help-text">
        Overblikket i Projekt Builder vil ikke træde i kraft på projektet før
        strukturen som helhed bliver gemt. Værktøjet giver muligheden for at
        foretage flere ændringer og danne sig et overblik over resultatet før
        strukturen bliver gemt.
      </span>
      <span className="help-text">
        Når resultatet er tilfredsstillende og strukturen er opbygget som
        planlagt kan den gemmes vha. <strong>Gem</strong> knappen i vinduets
        øverste venstre hjørne. Alle ændringer vil øjeblikkeligt træde i kraft.
      </span>

      <span className="help-sub-head">Eksisterende mapper</span>
      <span className="help-text">
        Eksisterende mapper, der allerede er en aktiv del af arkivet, har visse
        begrænsninger i Projekt Builder.
      </span>
      <span className="help-text">
        <strong>Du kan:</strong>
        <br />
        • Omdøbe navnet på en mappe
        <br />
        • Redigere mappernes beskrivelse
        <br />
        • Tilføje nye undermapper
        <br />
        • Importere mappestrukturer fra f.eks. et andet projekt eller en
        skabelon
        <br />• Flytte nyoprettede mapper til dem
      </span>
      <span className="help-text">
        <strong>Du kan ikke:</strong>
        <br />
        • Ændre den aktive struktur og mappehierarkiet på allerede aktive
        arkivmapper
        <br />• Fjerne allerede aktive arkivmapper
      </span>
      <span className="help-text">
        Ændringer til allerede aktive arkivmapper foretaget i Projekt Builder
        vil først træde i kraft når strukturen gemmes via <strong>Gem</strong>{' '}
        knappen.
      </span>

      <span className="help-sub-head">Oprettelse af nye mapper</span>
      <span className="help-text">
        Vælg <strong>+ Ny mappe</strong> i øverste højre hjørne for at oprette
        en ny mappe i roden af det aktuelle projekt.
      </span>
      <span className="help-text">
        Du vil blive bedt om at indtaste:
        <br />
        • Navn (påkrævet)
        <br />• Beskrivelse (valgfrit)
      </span>
      <span className="help-text">
        Den nye mappe vil blive oprettet i Projekt Builder og være synlig i
        strukturen med det samme når den bliver gemt
        <br />
        Ændringer i form af tilføjelse af nye mapper foretaget i Projekt Builder
        vil først træde i kraft når strukturen gemmes via <strong>
          Gem
        </strong>{' '}
        knappen.
      </span>

      <span className="help-sub-head">Import af mappestrukturer</span>
      <span className="help-text">
        Importfunktionen giver dig mulighed for at kopiere en eksisterende
        mappestruktur fra et andet projekt eller en skabelon.
      </span>

      <span className="help-sub-head">Trin 1 - Vælg en kilde</span>
      <span className="help-text">
        Vælg det projekt eller den skabelon, du vil importere fra.
      </span>
      <span className="help-sub-head">Trin 2 - Vælg mapper</span>
      <span className="help-text">
        Gennemse den tilgængelige mappestruktur, og vælg de mapper, du vil
        importere vha. afrydningsfelterne.
        <br />
        • Valg af en mappe inkluderer automatisk alle dens undermapper.
        <br />• Undermapper kan vælges uafhængigt af mappehierarkiet
      </span>
      <span className="help-text">
        <strong>Brug:</strong>
        <br />• <strong>Vælg alle</strong> for at inkludere hele strukturen.
        <br />• <strong>Ryd valg</strong> for at rydde markering.
      </span>
      <span className="help-text">
        <strong>Overblik i skabelonstruktur:</strong>
        <br />• <strong>Åben alle</strong> vil åbne alle mapper for at give
        overblik over alle undermapper.
        <br />• <strong>Luk alle</strong> vil lukke alle mapper så kun
        rodmapperne er sylige, hver enkelt mappe kan derefter blive manuelt
        åbnet.
      </span>
      <span className="help-text">
        Når den ønskede struktur er valgt vil den blive importeret til Projekt
        Builder og være synlig i strukturen via knappen:{' '}
        <strong>Importer til Projekt Builder</strong>
        <br />
        De nyligt oprettede mapper kan flyttes rundt og redigeres, ændringer i
        form af import af mappestruktur foretaget i Projekt Builder vil først
        træde i kraft når strukturen gemmes via <strong>Gem</strong> knappen.
      </span>

      <span className="help-sub-head">
        Tilføjelse af mapper i en anden mappe (undermapper)
      </span>
      <span className="help-text">
        I stedet for at tilføje mapper til projektroden og senere flytte dem til
        deres ønskede lokation, kan undermapper i stedet oprettes direkte i den
        mappe hvor de skal ende op.
        <br />
        Det er muligt både at oprette nye mapper samt at importere struktur
        direkte til både nyligt oprettede mapper samt mapper der allerede er
        aktive arkivmapper
      </span>
      <span className="help-text">
        En mappes kontekstmenu kan åbnes ved:
        <br />
        • Højreklikke på mappen, eller
        <br />• Vælge <strong>⋮</strong>-menuen i højre side af rækken.
      </span>
      <span className="help-text">
        Vælg enten:
        <br />• <strong>Ny mappe</strong>
        <br />• <strong>Importer</strong>
      </span>
      <span className="help-text">
        Den valgte mappe bliver destinationen, og dialogboksen viser
        målplaceringen, før mappen oprettes eller importeres.
        <br />
        Alle regner der gælder for mapper der er oprettet eller importeret via
        knapperne i toppen af vindue vil også gælde for mapper oprettet vha. den
        målrettede version af funktionaliteterne.
      </span>

      <span className="help-sub-head">Redigering af mapper</span>
      <span className="help-text">
        Alle mapper kan redigeres, før de gemmes. Vælg <strong>Rediger</strong>{' '}
        i mappens kontekstmenu for at ændre:
        <br />
        • Navn
        <br />• Beskrivelse
      </span>
      <span className="help-text">
        Ændringer i form af redigering af mapper foretaget i Projekt Builder vil
        først træde i kraft når strukturen gemmes via <strong>Gem</strong>{' '}
        knappen.
      </span>

      <span className="help-sub-head">Flytning af nye mapper</span>
      <span className="help-text">
        Alle nyoprettede mappe kan flyttes rundt i strukturen før ændringerne
        gemmes.
      </span>
      <span className="help-text">
        Du kan flytte dem ved at:
        <br />
        • Trække og slippe mappen til en ny overordnet mappe
        <br />• Vælge <strong>Flyt til</strong> i mappens kontekstmenu
        <br />
        Du kan søge efter nyoprettede mapper, der skal være placeret i en given
        lokation via <strong>Hiv hertil</strong> knappen i en mappens
        kontekstmenu.
      </span>
      <span className="help-text">
        Når en mappe flyttes vil alle undermapper automatisk følge med.
        <br />
        Mapper der allerede er aktive i arkivet kan ikke flyttes via Projekt
        Builder værktøjet.
      </span>

      <span className="help-sub-head">Fjerne nye mapper</span>
      <span className="help-text">
        Alle nytilføjede mapper kan fjernes fra Projekt builder, Vælg{' '}
        <strong>Fjern</strong> i mappens kontekstmenu for at slette den valgte
        mappe.
        <br />
        Alle undermapper vil automatisk blive fjernet, når en overmappe bliver
        fjernet.
      </span>
      <span className="help-text">
        Mapper der allerede er aktive i arkivet kan ikke fjernes via Projekt
        Builder værktøjet.
      </span>

      <span className="help-sub-head">Gemme ændringerne</span>
      <span className="help-text">
        Alle tilføjelser/ændringer foretaget vil være synlige og tilgængelige i
        Projekt Builder.
        <br />
        Værktøjet vil afvente at strukturen der er under behandling bliver meldt
        færdig, der kan foretages løbende ændringer indtil den bliver endeligt
        gemt:
        <br />
        • Nye mapper
        <br />
        • Importerede mapper
        <br />
        • Omdøbte mapper
        <br />
        • Opdaterede beskrivelser
        <br />• Flyttede mapper
      </span>
      <span className="help-text">
        Alle ændringer vil blive gemt på én når der trykkes på{' '}
        <strong>Gem</strong> knappen.
        <br />
        Ændringer kan ignoreres ved at forlade Projekt Builder værktøjet.
      </span>

      <span className="help-sub-head">Tips</span>
      <span className="help-text">
        • Gennemgå den komplette mappestruktur, før du gemmer.
        <br />
        • Eksisterende mapper forbliver fastlåst på deres oprindelige placering.
        <br />
        • Nye mapper kan omorganiseres frit, indtil de gemmes.
        <br />
        • Importerede mapper fungerer som nyoprettede mapper, indtil de gemmes.
        <br />• Brug kontekstmenuen til hurtigt at tilføje, redigere, flytte
        eller fjerne mapper uden at forlade den aktuelle visning.
      </span>
    </div>
  </Fragment>
);

const getHelpComponent = (locale: string) => {
  switch (locale) {
    case 'da':
      return <HelpDA />;
    default:
      return <HelpEN />;
  }
};

export const ProjectBuilderHelpInfoModal = ({
  visible,
  onClose,
  headerId,
}: HelpModalProps) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  return (
    <Modal
      visible={visible}
      title={intl.formatMessage({ id: headerId })}
      onCancel={onClose}
      footer={null}
      width={700}>
      <div css={styles.helpContent}>{getHelpComponent(intl.locale)}</div>
    </Modal>
  );
};

export const styleFn: ThemedStyleFn = (theme) => ({
  helpContent: {
    '.help-head': {
      display: 'block',
      fontSize: 18,
      fontWeight: 'bold',
      marginTop: 18,
      marginBottom: 8,
      color: theme.colors.darkGray ?? '#333',
    },
    '.help-sub-head': {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 600,
      marginTop: 14,
      color: theme.colors.black ?? '#444',
      gap: 8,
    },
    '.help-text': {
      display: 'block',
      fontSize: 14,
      margin: '4px 0 8px',
      color: theme.colors.black ?? '#666',
    },
  },
});
